/**
 * Every word this module puts on a screen (FR-16).
 *
 * Generated out of artboards/canvas-copy.json, which was read out of the
 * fifteen phase 1 artboards. Nothing here was invented at the keyboard. The
 * strings the canvas cannot carry are marked settledNotDrawn in the inventory
 * and in the comment above them, each with the reason no drawing can hold it;
 * none of them is an open question any more. There were five until 9 September
 * 2026, the three singular count forms made eight, and the four surfaces that
 * render a count of people who searched made thirteen later the same day. So
 * read the number off `npm run lint:copy`, which names every one of them on
 * every run, rather than off this line.
 *
 * scripts/check-copy-against-canvas.mjs holds this file to that inventory,
 * byte for byte, and holds the inventory to the drawing. It also runs
 * artboards/copy-lexicon.json over these values, so a wording that was
 * settled and a variant that was rejected cannot swap places again.
 *
 * Two kinds of word are deliberately absent. The kinds a reporter picks from
 * and the eight state words are rows (submission_type, status_label), because
 * a second customer wants other words or another number of them (FR-5, FR-5a).
 * And nothing here is a name, a date, a count or a title: those come from the
 * database, and inventing one is what a mockup may not do.
 */

export const copy = {
  panel: {
    title: "Feedback on this app",
    subtitle: "About the software, not about a machine.",
    // settledNotDrawn. Ten artboards draw the cross and none gives it a word, because an accessible name is not visible text and no drawing can hold one. An icon-only control still needs it (WCAG 2.4.6, coding standards 11.9), so blank was never one of the options.
    close: "Close",
  },
  launcher: {
    // The floating launcher, drawn on all four full screens as a dark pill with a speech bubble and this word beside it.
    label: "Feedback",
  },
  search: {
    // FR-31. A visible label, not a placeholder beside a magnifier.
    label: "Search the help",
    // The description slot, drawn under the field since 9 September. It was inside the field, which is a placeholder, and TextField has no such prop on purpose: a placeholder goes away as somebody types and is read as a value that is already there.
    description: "What are you trying to do?",
  },
  start: {
    helpHeading: "Help for this app",
  },
  results: {
    heading: "Might this help?",
    noneOfThese: "None of these?",
  },
  action: {
    // copy-lexicon.json: the way out of the help that puts something on the triage queue. Not the same action as asking for a how-to.
    reportInstead: "Report it instead",
    // copy-lexicon.json: the other way out, which lands on the wiki gap list (FR-28) and not on the fault queue.
    askForAHowTo: "Ask for a how-to",
    // copy-lexicon.json: the button that starts a submission from a list view, where there is no offer to decline.
    reportSomething: "Report something",
  },
  noResult: {
    title: "Nothing written about this yet",
    body: "Your search is on the list, so somebody knows it is missing. Say what you were trying to do and it gets written.",
    askForIt: "Ask for it",
    // copy-lexicon.json fixes the wording for this meaning as "people searched
    // for this". The number renders as a digit. It read "searched for this
    // too." until 9 September 2026, and "too" was the word that made a total
    // read as a count of other people: also_searched is the people who searched
    // these words and found nothing, and the write puts this reader among them,
    // so the sentence claimed one person too many at every value and was
    // flatly false at one, where the only searcher is the reader himself.
    peopleSearched: "{count} people searched for this",
    // settledNotDrawn. FR-30. The form for a count of one, which the plural
    // above rendered as "1 people". Not an edge: a search for words nobody has
    // searched before comes back as exactly one, so one is the floor of this
    // count rather than the far end of it.
    peopleSearchedSingular: "{count} person searched for this",
    // settledNotDrawn. FR-30. The count of one where that one person is the
    // reader, which is what the write answers on the first search for any new
    // words. No number in it: naming the reader and counting him in the same
    // breath is the shape list.sentInByYou exists to avoid.
    nobodyElseSearched: "Nobody else has searched for this yet",
  },
  article: {
    back: "Back to help",
    // FR-26: an article carries an owner and the date it was last checked, and the reader sees both.
    meta: "Kept by {owner} · last checked {date}",
    stillStuck: "Still stuck?",
  },
  form: {
    kindLabel: "What is this?",
    bodyLabel: "What happened?",
    // FR-21. The panel makes the image itself, so there is no drop zone and nothing arrives from the reporter's disk.
    screenshot: "Include a screenshot of this page",
    send: "Send it",
    // settledNotDrawn. Button.pendingLabel takes the place of send while one is in flight, so a sheet drawing this word is a sheet not drawing Send it. A word rather than a spinner, because a spinner says nothing to a screen reader.
    sending: "Sending",
    cancel: "Cancel",
    // One of the five strings spec section 12 records as not coming from the canvas of 4 September. It is on the canvas now.
    promise: "The answer comes back here.",
  },
  context: {
    heading: "Sent along automatically",
    you: "You",
    application: "Application",
    screen: "Screen",
    version: "Version",
  },
  sent: {
    title: "That is with us",
    body: "The answer comes back here, so you do not have to keep this open or remember to look.",
  },
  thread: {
    // copy-lexicon.json: the reader's own list is called From you everywhere, so the back link uses the word the tab pair taught.
    back: "From you",
    nobodyAnswered: "Nobody has answered yet",
    // The composer's visible label, above the field since 9 September. It was grey text inside the field, and TextArea takes no placeholder either.
    compose: "Add something",
    // Drawn on the send control on Sent and Thread. It cannot borrow Send it, which is the form's submit and means something else.
    sendMessage: "Send",
    whereItWent: "Where it went",
    liveWhen: "Live when",
    notLiveYet: "Not yet, you will see it here",
    // The reporter's own message is signed You, the triager's with the team name from the message row.
    fromReporter: "You",
  },
  error: {
    title: "That did not send",
    body: "Your text is still here. Try again, and if it keeps failing it is worth telling somebody the old way.",
    tryAgain: "Try again",
  },
  list: {
    tabEverything: "Everything",
    tabFromYou: "From you",
    // FR-30. The count is always the total, so a reader never has to work out whether it includes him.
    sentInBy: "Sent in by {count} people",
    // settledNotDrawn. FR-30. The form for a count of one, which the plural
    // above rendered as "1 people" until 9 September 2026. Every count on the
    // canvas is above one, so no sheet drew this and nothing asked the
    // question; drawing it would mean replacing the plural on a card that can
    // only ever show one of the two forms.
    sentInBySingular: "Sent in by {count} person",
    // settledNotDrawn. FR-30. The count of one where that one person is the
    // reader, which sent_in_by makes the likeliest row on From you: it is the
    // total including this submission, so an item nobody else has sent in is a
    // count of one that is him. It takes no includingYou, because naming him
    // and then telling him he is counted reads as two people.
    sentInByYou: "Sent in by you",
    // FR-30. Appended when the reader is one of them, and never any other
    // wording, on the lists and on the no-result screen alike.
    includingYou: ", including you",
    // Drawn as "1 answer to read".
    answersToRead: "{count} answer to read",
    // Drawn on the answered card of From you, beside the card that carries the singular. One card can only ever show one of the two forms.
    answersToReadPlural: "{count} answers to read",
  },
  empty: {
    title: "Nothing from you yet",
    body: "When something in this app is broken or missing, say it here. It comes back to this panel with an answer.",
  },
  screen: {
    title: "Feedback",
    subtitleEverything: "What we are building, what is broken, and what you asked for.",
    subtitleRoadmap: "What we are building, what we are not, and what you asked for. Nothing we have yet to build carries a date.",
    subtitleQueue: "The queue is everything nobody has answered yet, across every application.",
    tabRoadmap: "Roadmap",
    // Not Triage: the maintenance application already has a navigation row by that name and it is about fault reports.
    tabQueue: "Queue",
  },
  filter: {
    all: "All",
    issues: "Issues",
    ideas: "Ideas",
    planned: "Planned",
    shipped: "Shipped",
    // The name of the filter row, which is six controls that switch one setting and so is a group. Drawn on Main since 9 September.
    legend: "Show",
  },
  everything: {
    // copy-lexicon.json: the meta line under FIX READY. Not "Live this week", which is the wallboard's past tense.
    mergedNotLive: "Merged, not live yet",
    // The meta line under NO CHANGE. It points at the thread, where the triager's answer is.
    reasonInYourPanel: "The reason is in your panel",
    liveSince: "Live since {date}",
  },
  roadmap: {
    beingBuilt: "Being built",
    nextUp: "Next up",
    later: "Later",
    liveSinceLastWeek: "Live since last week",
    everythingShipped: "Everything that shipped",
    // A line under the three columns, not a fourth column. Spec decision 17.
    decidedNotTo: "What we decided not to do",
  },
  help: {
    title: "Help",
    subtitle: "How to do things in this application. If it is not here, say so and it gets written.",
    // FR-26a. The measured gap list, which is the same row FR-28 records.
    gapsHeading: "Asked for, not written yet",
    // FR-29.
    staleHeading: "Written, not checked lately",
    // The form used when the searches came from more than one screen.
    gapMostlyOn: "{count} people searched for this, mostly on {screen}",
    // settledNotDrawn. The count of one for that same form, and it is reachable
    // rather than theoretical: spread is true when the searches came from more
    // than one screen, and one person who looked for the same words on two
    // screens is one person and two screens. Measured on 9 September 2026
    // against GET /help/gaps, which answered people 1 with spread true.
    gapMostlyOnSingular: "{count} person searched for this, mostly on {screen}",
    // The form used when every search came from the same screen.
    gapOn: "{count} people searched for this, on {screen}",
    // settledNotDrawn. The count of one, which is where a gap list starts: the
    // first person to look for something nobody has written is a topic with one
    // person on it, and the plural rendered that as "1 people". Drawing it would
    // mean replacing the plural on the sheet, since a row holds one sentence.
    gapOnSingular: "{count} person searched for this, on {screen}",
    notHereTitle: "Not here?",
    notHereBody: "Say what you were looking for. It lands on the list above, and somebody writes it.",
  },
  queue: {
    // FR-25. The picker belongs on this surface and nowhere else: a reporter's list is pinned to his own application by his token.
    allApplications: "All applications",
    // The visible name on the picker, which is the same word the context block uses.
    applicationLabel: "Application",
    screenshot: "Screenshot",
    // The variant where the reporter left the box unticked, drawn on the duplicate sheet. The space stays the same size so the buttons below it do not jump.
    noScreenshot: "No screenshot sent",
    sentInByLabel: "Sent in by",
    // The value half of the Sent in by pair in the queue's fact row, which the drawing splits into a label and a value while the lists write it as one sentence. Same count and same meaning as list.sentInBy (FR-30).
    sentInByCount: "{count} people",
    // settledNotDrawn. The count of one for that same fact row, drawn nowhere
    // for the same reason as list.sentInBySingular: the value slot holds one
    // string, so the singular would replace the plural rather than sit beside
    // it. Two forms here where the lists need three, because a QueueSubmission
    // carries sentInBy and no includesYou.
    sentInByCountSingular: "{count} person",
    titleLabel: "Title people will read",
    // FR-2a. The reporter's own words are never edited; the title beside them may be.
    titleHelp: "Filled from the first line of the report. Rewrite it so somebody who was not there understands it. The words above stay as they were sent.",
    dispositionLabel: "What happens with it",
    answerLabel: "What the reporter gets to read",
    destinationLabel: "Where the work goes",
    // FR-14. A duplicate points at its original and both stay, so Already reported needs a reference of its own rather than the destination field, which carries a different kind of value. Drawn on TriageDuplicate, which is a second sheet because this component renders the destination field or this one and never both.
    duplicateLabel: "Which one it repeats",
    // The counterpart of queue.destinationHelp for the other outcome that needs a reference. It says the thing a triager worries about, which is that marking something a duplicate does not throw it away.
    duplicateHelp: "Both stay, and the reporter is sent to the one that was first.",
    // FR-11. It is why Save and answer stays disabled until a destination is typed.
    destinationHelp: "An accepted item needs somewhere to go before it can be saved.",
    save: "Save and answer",
    // Button.pendingLabel while the outcome is being written, drawn on the duplicate sheet with the save in flight.
    saving: "Saving",
    skip: "Skip for now",
    // Drawn as "waiting 1 day".
    waitingDay: "waiting {count} day",
    // Drawn as "waiting 2 days".
    waitingDays: "waiting {count} days",
    // FR-28. The meta line on a SEARCH MISS row in the queue.
    howToMissing: "A how-to is missing",
    searchMiss: "{count} people searched \"{terms}\" from {screen}",
    // settledNotDrawn. The count of one on that row. The queue groups the same
    // rows the gap list groups, so a topic exists because at least one person
    // looked for it: one is the floor here and zero cannot render, which is why
    // this row needs a singular and not a guard. Measured on 9 September 2026
    // against GET /queue, which answered people 1 for a topic one person
    // searched, and no row at all when nobody had.
    searchMissSingular: "{count} person searched \"{terms}\" from {screen}",
    // settledNotDrawn. FR-13 says the scheduled task says nothing when the queue is empty, so an empty queue is the good state and not a gap. No prompt and no illustration. The canvas draws no empty queue.
    emptyTitle: "Nothing is waiting",
    // settledNotDrawn. The date under the empty line, and the other half of an either/or that no single drawing can carry.
    emptyLastAnswered: "Last answered {date}",
    // settledNotDrawn. The first day of an installation has an empty queue and nothing answered behind it, so Last answered has nothing to show.
    emptyNothingAnsweredYet: "Nothing answered yet",
  },
  disposition: {
    // The four outcomes a triager can give are mechanism, not tenant wording: the code branches on them (FR-8). Drawn in sentence case on the buttons, which is what separates them from the chips a reporter reads.
    accepted: "Accepted",
    declined: "No change",
    duplicate: "Already reported",
    answered: "Answered",
  },
} as const;

export type Copy = typeof copy;
